'use client';

import React, { useState, useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,  } from 'recharts';
import { BookOpen, BarChart3, Zap } from 'lucide-react';
import { MaturityData, InstantMarket } from '../data/initialData';

interface CreditbookPanelProps {
  data: MaturityData;
}

const formatUSD = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toLocaleString()}`;
};

const formatUSDCompact = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
};

// ── Protocol color map ───────────────────────────────────────────────────────

const PROTOCOL_COLORS: Record<string, string> = {
  Aave: '#B6509E',
  Morpho: '#2470FF',
  Euler: '#E8612C',
};

const getProtocolColor = (name: string) => PROTOCOL_COLORS[name] ?? '#6C47FF';

// ── Instant Benchmark Panel ──────────────────────────────────────────────────

interface InstantChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const InstantChartTooltip = ({ active, payload, label }: InstantChartTooltipProps) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[180px]">
      <p className="text-xs font-semibold text-gray-700 mb-2">{label}</p>
      {payload.map((p: any) =>
        p.value > 0 ? (
          <div key={p.dataKey} className="flex justify-between gap-3 text-xs">
            <span className="flex items-center gap-1.5" style={{ color: p.fill }}>
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.fill }} />
              {p.name}
            </span>
            <span className="font-mono font-semibold text-gray-800">{formatUSD(p.value)}</span>
          </div>
        ) : null
      )}
    </div>
  );
};

function InstantBenchmarkPanel({ data }: { data: MaturityData }) {
  const markets: InstantMarket[] = data.instantMarkets && data.instantMarkets.length > 0
    ? data.instantMarkets
    : [];

  // Compute directly from source markets — never rely on stored data.apr for display
  const totalSupply = markets.reduce((s, m) => s + m.totalSupply, 0);
  const totalBorrow = markets.reduce((s, m) => s + m.totalBorrowed, 0);
  const totalWeight = markets.reduce((s, m) => s + m.totalBorrowed, 0);
  const weightedAPR = totalWeight > 0
    ? markets.reduce((s, m) => s + m.rate * m.totalBorrowed, 0) / totalWeight
    : 0;

  if (markets.length === 0) {
    return (
      <div className="animate-slide-up">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div className="bg-[#EEE9FF] rounded-lg p-2">
            <Zap size={16} className="text-[#6C47FF]" />
          </div>
          <div>
            <p className="section-label">Instant Market</p>
            <h3 className="text-base font-semibold text-gray-900">Floating-Rate Benchmark Rates</h3>
          </div>
        </div>
        <div className="p-6 text-center text-sm text-gray-400 py-12">
          No source data entered yet. Add protocol data on the{' '}
          <a href="/yield-curve-simulator/edit" className="text-[#6C47FF] font-medium hover:underline">Edit Page</a>.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <div className="bg-[#EEE9FF] rounded-lg p-2">
          <Zap size={16} className="text-[#6C47FF]" />
        </div>
        <div>
          <p className="section-label">Instant Market</p>
          <h3 className="text-base font-semibold text-gray-900">Floating-Rate Benchmark Rates</h3>
        </div>
        <div className="ml-auto flex flex-col items-end">
          <span className="text-xs text-gray-400 font-medium">Weighted APR</span>
          <span className="text-lg font-mono font-bold text-[#6C47FF]">{weightedAPR.toFixed(2)}%</span>
        </div>
      </div>

      {/* Benchmark table */}
      <div className="p-6">
        <p className="text-xs text-gray-400 mb-4">
          Instant liquidity is sourced from onchain floating-rate lending markets. No fixed-term order book exists for this maturity.
        </p>

        {/* Table header */}
        <div className="grid grid-cols-[1fr_80px_1fr_1fr_80px] gap-3 px-4 mb-2">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Protocol</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Rate</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Total Supply</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Total Borrowed</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Util %</span>
        </div>

        {/* Protocol rows */}
        <div className="space-y-2">
          {markets.map((market) => {
            const color = getProtocolColor(market.protocol);
            const util = market.totalSupply > 0
              ? ((market.totalBorrowed / market.totalSupply) * 100).toFixed(1)
              : '0.0';
            const utilNum = parseFloat(util);

            return (
              <div
                key={market.protocol}
                className="grid grid-cols-[1fr_80px_1fr_1fr_80px] gap-3 items-center bg-white border border-gray-100 rounded-xl px-4 py-3.5 hover:border-gray-200 transition-colors shadow-sm"
              >
                {/* Protocol */}
                <div className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-sm font-semibold text-gray-800">{market.protocol}</span>
                </div>
                {/* Rate */}
                <div className="text-right">
                  <span className="text-sm font-mono font-bold text-gray-900">{market.rate.toFixed(1)}%</span>
                </div>
                {/* Total Supply */}
                <div className="text-right">
                  <span className="text-sm font-mono text-[#6C47FF] font-semibold">{formatUSD(market.totalSupply)}</span>
                </div>
                {/* Total Borrowed */}
                <div className="text-right">
                  <span className="text-sm font-mono text-orange-600 font-semibold">{formatUSD(market.totalBorrowed)}</span>
                </div>
                {/* Utilization */}
                <div className="text-right">
                  <span className={`text-sm font-mono font-semibold ${utilNum > 85 ? 'text-red-600' : utilNum > 70 ? 'text-amber-600' : 'text-gray-600'}`}>
                    {util}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals row */}
        <div className="grid grid-cols-[1fr_80px_1fr_1fr_80px] gap-3 items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mt-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Total</span>
          <div className="text-right">
            <span className="text-sm font-mono font-bold text-[#6C47FF]">{weightedAPR.toFixed(2)}%</span>
          </div>
          <div className="text-right">
            <span className="text-sm font-mono font-bold text-[#6C47FF]">{formatUSD(totalSupply)}</span>
          </div>
          <div className="text-right">
            <span className="text-sm font-mono font-bold text-orange-600">{formatUSD(totalBorrow)}</span>
          </div>
          <div className="text-right">
            <span className="text-sm font-mono font-semibold text-gray-600">
              {totalSupply > 0 ? ((totalBorrow / totalSupply) * 100).toFixed(1) : '0.0'}%
            </span>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-3">
          Rates are indicative. Weighted APR calculated by total borrowed amount. Utilization = Total Borrowed / Total Supply.
        </p>
      </div>

    </div>
  );
}

// ── Depth Chart (secondary) ──────────────────────────────────────────────────

import {
  AreaChart,
  Area,
  ReferenceLine,
} from 'recharts';

interface DepthChartData {
  rate: number;
  available: number;
  borrowed: number;
}

const DepthTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[200px]">
      <p className="text-xs font-semibold text-gray-700 mb-2 font-mono">{Number(label).toFixed(2)}% APR</p>
      {payload.map((p: any) =>
        p.value > 0 ? (
          <div key={p.dataKey} className="flex justify-between gap-3 text-xs">
            <span className="flex items-center gap-1.5" style={{ color: p.color }}>
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
              {p.name}
            </span>
            <span className="font-mono font-semibold text-gray-800">{formatUSD(p.value)}</span>
          </div>
        ) : null
      )}
    </div>
  );
};

function DepthChart({ data, currentRate }: { data: DepthChartData[]; currentRate: number }) {
  const rateMin = data.length > 0 ? data[0].rate - 0.3 : 0;
  const rateMax = data.length > 0 ? data[data.length - 1].rate + 0.3 : 10;

  return (
    <div style={{ height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <defs>
            <linearGradient id="availableGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6C47FF" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#6C47FF" stopOpacity={0.04} />
            </linearGradient>
            <linearGradient id="borrowedGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9CA3AF" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#9CA3AF" stopOpacity={0.04} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.7} vertical={false} />
          <XAxis
            dataKey="rate"
            type="number"
            domain={[rateMin, rateMax]}
            tickFormatter={(v) => `${Number(v).toFixed(1)}%`}
            tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', fill: '#9CA3AF' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatUSDCompact}
            tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip content={<DepthTooltip />} />
          <ReferenceLine
            x={currentRate}
            stroke="#6C47FF"
            strokeWidth={1.5}
            strokeDasharray="4 4"
            label={{
              value: `${currentRate.toFixed(2)}%`,
              position: 'top',
              fontSize: 11,
              fill: '#6C47FF',
              fontFamily: 'IBM Plex Mono, monospace',
              fontWeight: 600,
            }}
          />
          <Area
            type="stepAfter"
            dataKey="borrowed"
            name="Borrowed / Filled"
            stroke="#9CA3AF"
            strokeWidth={1.5}
            fill="url(#borrowedGradient)"
            isAnimationActive={true}
            animationDuration={400}
          />
          <Area
            type="stepAfter"
            dataKey="available"
            name="Available Lender Liquidity"
            stroke="#6C47FF"
            strokeWidth={2}
            fill="url(#availableGradient)"
            isAnimationActive={true}
            animationDuration={400}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Order Ladder ─────────────────────────────────────────────────────────────

type RowStatus = 'Borrowed' | 'Current Rate' | 'Available';

interface LadderRow {
  rate: number;
  size: number;
  cumulative: number;
  status: RowStatus;
  isCurrentRate?: boolean;
}

interface OrderLadderProps {
  rows: LadderRow[];
  currentRate: number;
}

function OrderLadder({ rows, currentRate }: OrderLadderProps) {
  const sorted = [...rows].sort((a, b) => b.rate - a.rate);

  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-[1fr_1fr_1fr_100px] gap-0 mb-1 px-3">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Rate</span>
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Size</span>
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Cum. Depth</span>
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Status</span>
      </div>

      <div className="space-y-0.5 max-h-80 overflow-y-auto pr-0.5">
        {sorted.map((row, i) => {
          const isCurrentRate = row.isCurrentRate;
          const isBorrowed = row.status === 'Borrowed';
          const isAvailable = row.status === 'Available';

          if (isCurrentRate) {
            return (
              <div
                key={`current-${i}`}
                className="grid grid-cols-[1fr_1fr_1fr_100px] gap-0 px-3 py-2.5 rounded-lg bg-[#6C47FF]/10 border border-[#6C47FF]/25 my-1"
              >
                <span className="text-sm font-mono font-bold text-[#6C47FF]">{row.rate.toFixed(2)}%</span>
                <span className="text-sm font-mono text-[#6C47FF]/80 text-right">—</span>
                <span className="text-sm font-mono text-[#6C47FF]/80 text-right">—</span>
                <span className="text-right">
                  <span className="inline-block text-xs font-semibold text-[#6C47FF] bg-[#6C47FF]/10 border border-[#6C47FF]/30 rounded-md px-2 py-0.5">
                    Current Rate
                  </span>
                </span>
              </div>
            );
          }

          return (
            <div
              key={`row-${i}`}
              className={`grid grid-cols-[1fr_1fr_1fr_100px] gap-0 px-3 py-2 rounded-lg transition-colors ${
                isAvailable ? 'hover:bg-[#6C47FF]/5' : 'hover:bg-gray-50/80'
              }`}
            >
              <span className={`text-sm font-mono font-semibold ${isAvailable ? 'text-[#6C47FF]' : 'text-gray-400'}`}>
                {row.rate.toFixed(2)}%
              </span>
              <span className={`text-sm font-mono text-right ${isAvailable ? 'text-gray-700' : 'text-gray-400'}`}>
                {formatUSD(row.size)}
              </span>
              <span className={`text-sm font-mono text-right ${isAvailable ? 'text-gray-500' : 'text-gray-400'}`}>
                {formatUSD(row.cumulative)}
              </span>
              <span className="text-right">
                {isAvailable ? (
                  <span className="inline-block text-xs font-semibold text-[#6C47FF] bg-[#EEE9FF] rounded-md px-2 py-0.5">Available</span>
                ) : (
                  <span className="inline-block text-xs font-semibold text-gray-400 bg-gray-100 rounded-md px-2 py-0.5">Borrowed</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 px-3">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-[#6C47FF] inline-block" />
          <span className="text-xs text-gray-500">Available — lender offers open in the book</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-gray-300 inline-block" />
          <span className="text-xs text-gray-400">Borrowed — levels already taken by borrowers</span>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function CreditbookPanel({ data }: CreditbookPanelProps) {
  const [showDepthChart, setShowDepthChart] = useState(false);
  const isInstant = data.maturity === 'Instant';

  const { ladderRows, depthChartData } = useMemo(() => {
    const currentRate = data.apr;
    const sorted = [...data.supplyOrders].sort((a, b) => a.rate - b.rate);

    let cum = 0;
    const withCum = sorted.map((o) => {
      cum += o.size;
      return { ...o, cumulative: cum };
    });

    const rows: LadderRow[] = withCum.map((o) => ({
      rate: o.rate,
      size: o.size,
      cumulative: o.cumulative,
      status: o.rate > currentRate ? 'Available' : 'Borrowed',
      isCurrentRate: false,
    }));

    const currentRateRow: LadderRow = {
      rate: currentRate,
      size: 0,
      cumulative: 0,
      status: 'Current Rate',
      isCurrentRate: true,
    };

    const hasExact = rows.some((r) => r.rate === currentRate);
    const allRows = hasExact ? rows : [...rows, currentRateRow];

    const allRates = allRows
      .filter((r) => !r.isCurrentRate)
      .map((r) => r.rate)
      .sort((a, b) => a - b);

    const depthData: DepthChartData[] = allRates.map((rate) => {
      const row = allRows.find((r) => r.rate === rate && !r.isCurrentRate);
      return {
        rate,
        available: row?.status === 'Available' ? (row?.cumulative ?? 0) : 0,
        borrowed: row?.status === 'Borrowed' ? (row?.cumulative ?? 0) : 0,
      };
    });

    return { ladderRows: allRows, depthChartData: depthData };
  }, [data]);

  const totalAvailable = data.supplyOrders
    .filter((o) => o.rate > data.apr)
    .reduce((s, o) => s + o.size, 0);

  const totalBorrowed = data.supplyOrders
    .filter((o) => o.rate <= data.apr)
    .reduce((s, o) => s + o.size, 0);

  if (isInstant) {
    return (
      <div className="spine-card overflow-hidden animate-slide-up">
        <InstantBenchmarkPanel data={data} />
      </div>
    );
  }

  return (
    <div className="spine-card overflow-hidden animate-slide-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="bg-[#EEE9FF] rounded-lg p-2">
            <BookOpen size={16} className="text-[#6C47FF]" />
          </div>
          <div>
            <p className="section-label">Creditbook Depth</p>
            <h3 className="text-base font-semibold text-gray-900">
              {data.maturity} Maturity — Order Ladder
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-4 mr-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#6C47FF] inline-block" />
              <span className="text-xs text-gray-500">Available</span>
              <span className="text-xs font-mono font-semibold text-[#6C47FF]">{formatUSD(totalAvailable)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-gray-300 inline-block" />
              <span className="text-xs text-gray-500">Borrowed</span>
              <span className="text-xs font-mono font-semibold text-gray-500">{formatUSD(totalBorrowed)}</span>
            </div>
          </div>

          <button
            onClick={() => setShowDepthChart((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 ${
              showDepthChart
                ? 'bg-[#6C47FF] text-white border-[#6C47FF]'
                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            <BarChart3 size={13} />
            Depth Chart
          </button>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <OrderLadder rows={ladderRows} currentRate={data.apr} />

        {showDepthChart && (
          <div className="pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Cumulative Depth Chart
            </p>
            <DepthChart data={depthChartData} currentRate={data.apr} />
          </div>
        )}
      </div>
    </div>
  );
}